/**
 * Vibe Remote Authentication Service
 *
 * Handles device authentication with Vibe Remote relay server via REST API.
 * Uses URL-based pairing flow for device authentication.
 *
 * Auth Flow:
 * 1. Call initiatePairing() to get a pairing token and URL
 * 2. Daemon displays pairing URL to user
 * 3. User clicks URL, Web UI calls approvePairing() to approve
 * 4. Daemon calls confirmWithCode() to get JWT tokens
 * 5. Use tokens for authenticated requests
 *
 * Endpoints (as implemented on server):
 * - POST /auth/pair/init     → Initiate pairing, get token + URL
 * - POST /auth/pair/approve  → Approve pairing (from Web UI)
 * - GET  /auth/pair/status/:token → Check pairing status
 * - POST /auth/pair/confirm  → Confirm with code, get JWT tokens
 * - POST /auth/refresh       → Refresh access token
 * - GET  /auth/status        → Check auth status with Bearer token
 */

// Default server URL
const DEFAULT_SERVER_URL = 'https://tools.eroland.me';

// ============================================
// Type Definitions
// ============================================

/**
 * Request for initiating pairing
 */
export interface InitiatePairingRequest {
  deviceId: string;
}

/**
 * Response from /auth/pair/init
 */
export interface InitiatePairingResponse {
  token: string;
  pairingUrl: string;
  expiresIn: number;
}

/**
 * Request for approving pairing
 */
export interface ApprovePairingRequest {
  token: string;
}

/**
 * Response from /auth/pair/approve
 */
export interface ApprovePairingResponse {
  success: boolean;
  message: string;
  deviceId?: string;
}

/**
 * Response from /auth/pair/status/:token
 */
export interface PairingStatusResponse {
  status: 'pending' | 'approved' | 'expired' | 'used';
  message: string;
  deviceId?: string;
}

/**
 * Request for confirming pairing with code
 */
export interface ConfirmPairingRequest {
  code: string;
}

/**
 * Response from /auth/pair/confirm
 */
export interface ConfirmPairingResponse {
  accessToken: string;
  refreshToken: string;
  deviceId: string;
  expiresIn: number;
}

/**
 * Request for refreshing token
 */
export interface RefreshTokenRequest {
  refreshToken: string;
}

/**
 * Response from /auth/refresh
 */
export interface RefreshTokenResponse {
  accessToken: string;
  expiresIn: number;
}

/**
 * Response from /auth/status
 */
export interface AuthStatusResponse {
  authenticated: boolean;
  deviceId?: string;
  sessionId?: string;
}

/**
 * Response from /auth/daemon-status
 */
export interface DaemonStatusResponse {
  daemonConnected: boolean;
  daemonCount: number;
  daemons: Array<{
    deviceId: string;
    status: 'ready' | 'busy';
    authenticated: boolean;
    sessionCount: number;
  }>;
}

/**
 * Device info returned from /auth/my-devices
 */
export interface MyDevice {
  id: string;
  name: string;
  relayUrl: string;
  isPaired: boolean;
  pairedAt: string;
  sessionId: string;
}

/**
 * Response from /auth/my-devices
 */
export interface MyDevicesResponse {
  authenticated: boolean;
  devices: MyDevice[];
}

/**
 * Generic error response from server
 */
export interface ErrorResponse {
  error: string;
  message?: string;
}

// ============================================
// API Functions
// ============================================

/**
 * Initiate pairing process
 *
 * Starts the device pairing flow. Returns a pairing token and URL.
 * The daemon should display this URL for the user to click.
 *
 * @param serverUrl - Relay server URL (default: https://tools.eroland.me)
 * @param deviceId - Optional device identifier
 * @returns Pairing token, URL, and expiry
 */
export async function initiatePairing(
  serverUrl: string = DEFAULT_SERVER_URL,
  deviceId?: string
): Promise<InitiatePairingResponse | null> {
  try {
    console.log('[VibeRemoteAuth] Initiating pairing...');

    const response = await fetch(`${serverUrl}/auth/pair/init`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        deviceId: deviceId || generateDeviceId(),
      }),
    });

    if (!response.ok) {
      const errorData: ErrorResponse = await response.json().catch(() => ({
        error: 'Unknown error',
      }));
      console.error('[VibeRemoteAuth] Initiate pairing failed:', response.status, errorData);
      return null;
    }

    const data: InitiatePairingResponse = await response.json();
    console.log('[VibeRemoteAuth] Pairing initiated, token:', data.token?.slice(0, 8) + '...');
    return data;
  } catch (error) {
    console.error('[VibeRemoteAuth] Error initiating pairing:', error);
    return null;
  }
}

/**
 * Approve a pairing request
 *
 * Called by the Web UI when the user clicks the pairing URL.
 * Marks the pairing as approved so the daemon can complete it.
 *
 * @param serverUrl - Relay server URL
 * @param token - Pairing token from the URL query parameter
 * @returns Success status and device ID
 */
export async function approvePairing(
  serverUrl: string = DEFAULT_SERVER_URL,
  token: string
): Promise<ApprovePairingResponse | null> {
  try {
    console.log('[VibeRemoteAuth] Approving pairing...');

    const response = await fetch(`${serverUrl}/auth/pair/approve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token,
      }),
    });

    if (!response.ok) {
      const errorData: ErrorResponse = await response.json().catch(() => ({
        error: 'Unknown error',
      }));
      console.error('[VibeRemoteAuth] Approve pairing failed:', response.status, errorData);
      return null;
    }

    const data: ApprovePairingResponse = await response.json();
    console.log('[VibeRemoteAuth] Pairing approved:', data.success);
    return data;
  } catch (error) {
    console.error('[VibeRemoteAuth] Error approving pairing:', error);
    return null;
  }
}

/**
 * Check pairing status
 *
 * Polls this endpoint to check if a pairing request has been approved.
 *
 * @param serverUrl - Relay server URL
 * @param token - Pairing token
 * @returns Current pairing status
 */
export async function checkPairingStatus(
  serverUrl: string = DEFAULT_SERVER_URL,
  token: string
): Promise<PairingStatusResponse | null> {
  try {
    const response = await fetch(`${serverUrl}/auth/pair/status/${token}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData: ErrorResponse = await response.json().catch(() => ({
        error: 'Unknown error',
      }));
      console.error('[VibeRemoteAuth] Check pairing status failed:', response.status, errorData);
      return null;
    }

    const data: PairingStatusResponse = await response.json();
    console.log('[VibeRemoteAuth] Pairing status:', data.status);
    return data;
  } catch (error) {
    console.error('[VibeRemoteAuth] Error checking pairing status:', error);
    return null;
  }
}

/**
 * Confirm pairing with 6-digit code
 *
 * Called by the daemon after user approves pairing.
 * The daemon displays a 6-digit code that the user enters.
 *
 * @param serverUrl - Relay server URL
 * @param code - 6-digit confirmation code
 * @returns JWT tokens for authentication
 */
export async function confirmWithCode(
  serverUrl: string = DEFAULT_SERVER_URL,
  code: string
): Promise<ConfirmPairingResponse | null> {
  try {
    console.log('[VibeRemoteAuth] Confirming pairing with code...');

    const response = await fetch(`${serverUrl}/auth/pair/confirm`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        code,
      }),
    });

    if (!response.ok) {
      const errorData: ErrorResponse = await response.json().catch(() => ({
        error: 'Unknown error',
      }));
      console.error('[VibeRemoteAuth] Confirm pairing failed:', response.status, errorData);
      return null;
    }

    const data: ConfirmPairingResponse = await response.json();
    console.log('[VibeRemoteAuth] Pairing confirmed, device:', data.deviceId);
    return data;
  } catch (error) {
    console.error('[VibeRemoteAuth] Error confirming pairing:', error);
    return null;
  }
}

/**
 * Refresh access token
 *
 * Exchange a refresh token for a new access token.
 *
 * @param serverUrl - Relay server URL
 * @param refreshToken - The refresh token
 * @returns New access token and expiry
 */
export async function refreshAccessToken(
  serverUrl: string = DEFAULT_SERVER_URL,
  refreshToken: string
): Promise<RefreshTokenResponse | null> {
  try {
    console.log('[VibeRemoteAuth] Refreshing access token...');

    const response = await fetch(`${serverUrl}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refreshToken,
      }),
    });

    if (!response.ok) {
      const errorData: ErrorResponse = await response.json().catch(() => ({
        error: 'Unknown error',
      }));
      console.error('[VibeRemoteAuth] Refresh token failed:', response.status, errorData);
      return null;
    }

    const data: RefreshTokenResponse = await response.json();
    console.log('[VibeRemoteAuth] Access token refreshed');
    return data;
  } catch (error) {
    console.error('[VibeRemoteAuth] Error refreshing token:', error);
    return null;
  }
}

/**
 * Check authentication status
 *
 * Validates the current access token and returns auth status.
 *
 * @param serverUrl - Relay server URL
 * @param accessToken - Bearer access token
 * @returns Authentication status
 */
export async function checkAuthStatus(
  serverUrl: string = DEFAULT_SERVER_URL,
  accessToken: string
): Promise<AuthStatusResponse | null> {
  try {
    console.log('[VibeRemoteAuth] Checking auth status...');

    const response = await fetch(`${serverUrl}/auth/status`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const errorData: ErrorResponse = await response.json().catch(() => ({
        error: 'Unknown error',
      }));
      console.error('[VibeRemoteAuth] Check auth status failed:', response.status, errorData);
      return null;
    }

    const data: AuthStatusResponse = await response.json();
    console.log('[VibeRemoteAuth] Auth status:', data.authenticated);
    return data;
  } catch (error) {
    console.error('[VibeRemoteAuth] Error checking auth status:', error);
    return null;
  }
}

/**
 * Check if any daemon is currently connected to the relay
 *
 * This allows the Web UI to detect if a daemon is already connected
 * so it can skip the pairing flow and connect directly.
 *
 * @param serverUrl - Relay server URL (default: https://tools.eroland.me)
 * @returns Daemon status including list of connected daemons
 */
export async function checkDaemonStatus(
  serverUrl: string = DEFAULT_SERVER_URL
): Promise<DaemonStatusResponse> {
  try {
    console.log('[VibeRemoteAuth] Checking daemon status...');

    const response = await fetch(`${serverUrl}/auth/daemon-status`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      console.error('[VibeRemoteAuth] Check daemon status failed:', response.status);
      return { daemonConnected: false, daemonCount: 0, daemons: [] };
    }

    const data: DaemonStatusResponse = await response.json();
    console.log('[VibeRemoteAuth] Daemon status:', data.daemonConnected ? `${data.daemonCount} connected` : 'none connected');
    return data;
  } catch (error) {
    console.error('[VibeRemoteAuth] Error checking daemon status:', error);
    return { daemonConnected: false, daemonCount: 0, daemons: [] };
  }
}

/**
 * Get devices associated with the current authentication session
 *
 * This endpoint validates HTTP-only cookies set during pairing and returns
 * the device info. Used to sync CLI-paired devices to the Web UI Zustand store.
 *
 * Flow:
 * 1. Daemon pairs via CLI → relay sets HTTP-only cookies
 * 2. Web UI calls getMyDevices() on load
 * 3. If authenticated, relay returns device info
 * 4. Web UI registers device in Zustand store
 *
 * @param serverUrl - Relay server URL (default: https://tools.eroland.me)
 * @returns Device info if authenticated, or null/empty if not
 */
export async function getMyDevices(
  serverUrl: string = DEFAULT_SERVER_URL
): Promise<MyDevicesResponse> {
  try {
    console.log('[VibeRemoteAuth] Fetching my devices from relay...');

    const response = await fetch(`${serverUrl}/auth/my-devices`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      // CRITICAL: Include credentials to send HTTP-only cookies
      credentials: 'include',
    });

    if (!response.ok) {
      // 401 means not authenticated - expected when not paired
      if (response.status === 401) {
        console.log('[VibeRemoteAuth] Not authenticated - no paired devices');
        return { authenticated: false, devices: [] };
      }

      console.error('[VibeRemoteAuth] Get my devices failed:', response.status);
      return { authenticated: false, devices: [] };
    }

    const data: MyDevicesResponse = await response.json();
    console.log('[VibeRemoteAuth] My devices:', data.authenticated ? `${data.devices.length} device(s)` : 'not authenticated');
    return data;
  } catch (error) {
    console.error('[VibeRemoteAuth] Error getting my devices:', error);
    return { authenticated: false, devices: [] };
  }
}

// ============================================
// Utility Functions
// ============================================

/**
 * Generate a unique device identifier
 */
function generateDeviceId(): string {
  return `web-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Extract pairing token from URL query parameters
 *
 * @returns The pairing token if present, null otherwise
 */
export function getPairingTokenFromUrl(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('pair');
}

/**
 * Remove pairing token from URL without page reload
 * Called after successful pairing to clean up the URL
 */
export function clearPairingTokenFromUrl(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('pair');
  window.history.replaceState({}, document.title, url.toString());
}

/**
 * Build pairing URL for a given token
 *
 * @param baseUrl - Base URL of the Web UI
 * @param token - Pairing token
 * @returns Full pairing URL
 */
export function buildPairingUrl(baseUrl: string, token: string): string {
  const url = new URL(baseUrl);
  url.searchParams.set('pair', token);
  return url.toString();
}

// ============================================
// Viewer Token Functions (Cross-Device Access)
// ============================================

/**
 * Response from /auth/viewer/verify
 */
export interface ViewerVerifyResponse {
  success: boolean;
  deviceId?: string;
  sessionId?: string;
  error?: string;
}

/**
 * Extract viewer token from URL hash fragment
 *
 * The viewer token is stored in the hash fragment (#token=xxx) for privacy.
 * Hash fragments are not sent to the server in HTTP requests or logged.
 *
 * @returns The viewer token if present, null otherwise
 */
export function getViewerTokenFromUrl(): string | null {
  const hash = window.location.hash;
  if (!hash) return null;

  // Parse hash as query string: #token=xxx
  const match = hash.match(/[#&]token=([^&]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Remove viewer token from URL hash without page reload
 * Called after successful verification to clean up the URL
 */
export function clearViewerTokenFromUrl(): void {
  // Remove the token from hash while preserving other hash params
  const hash = window.location.hash;
  if (!hash) return;

  // Remove token param from hash
  const newHash = hash.replace(/[#&]?token=[^&]+/, '').replace(/^#&/, '#').replace(/^#$/, '');

  if (newHash) {
    window.history.replaceState(null, '', window.location.pathname + window.location.search + newHash);
  } else {
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }
}

/**
 * Verify a viewer token and receive authentication cookies
 *
 * This is the cross-device authentication flow:
 * 1. User opens bookmarked URL with #token=xxx
 * 2. Web UI extracts token and calls this function
 * 3. Server validates token, sets HTTP-only cookies
 * 4. Web UI receives success response, can now connect to relay
 *
 * @param serverUrl - Relay server URL (default: https://tools.eroland.me)
 * @param token - The viewer JWT token from the URL hash
 * @returns Verification result with deviceId on success, error on failure
 */
export async function verifyViewerToken(
  serverUrl: string = DEFAULT_SERVER_URL,
  token: string
): Promise<ViewerVerifyResponse> {
  try {
    console.log('[VibeRemoteAuth] Verifying viewer token...');

    const response = await fetch(`${serverUrl}/auth/viewer/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
      // CRITICAL: Include credentials to receive HTTP-only cookies
      credentials: 'include',
    });

    const data = await response.json();

    if (!response.ok) {
      console.error('[VibeRemoteAuth] Viewer token verification failed:', response.status, data);
      return {
        success: false,
        error: data.error || 'Token verification failed',
      };
    }

    console.log('[VibeRemoteAuth] Viewer token verified, device:', data.deviceId);
    return {
      success: true,
      deviceId: data.deviceId,
      sessionId: data.sessionId,
    };
  } catch (error) {
    console.error('[VibeRemoteAuth] Error verifying viewer token:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error',
    };
  }
}
